import React, { useEffect, useRef, useState } from "react";
import "./adminDashboard.css";
import Auth from "../../../../utils/auth";
import Tracking from "../../../../components/TrackingComp";
import {
  GET_USER_WEB_AUTHN_IDS,
  GET_TASK_FOR_USERS,
} from "../../../../utils/GraphQL/queries";
import { useLazyQuery } from "@apollo/client";
import WebAuthnCheckAndStore from "../../../../utils/WebAuthnCheckAndStore";
import AdminDashboardTasks from "./AdminDashboardTasks";
import { useMediaQuery } from "@mui/material";
import TaskDisplayModal from "../../adminComponents/ClientTasksDisplay/TaskModal";
import { useNavigate } from "react-router";
import { hasAnyRole } from '../../../../utils/permissions';

const AdminDashboard = () => {
  const navigate = useNavigate();
  /* null, not [] — "no tasks" and "not fetched yet" must not render the
     same. The list and the Overdue tile both branch on it. */
  const [tasks, setTasks] = useState(null);
  const [getUserWebAuthnIds] = useLazyQuery(GET_USER_WEB_AUTHN_IDS);
  const [openSingleTaskModal, setOpenSingleTaskModal] = useState(false);
  const [queryAllTasks, setQueryAllTasks] = useState(false);
  const [noRedirect, setNoRedirect] = useState(false);
  const [userPerm, setUserPerm] = useState("");
  const [userId, setUserId] = useState("");
  const [selectedSort, setSelectedSort] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  /* 899.98, not 900: AdminNavbar uses `min-width: 900px`, so at exactly
     900 both its desktop branch and this page's mobile branch were live. */
  const below900MQ = useMediaQuery("(max-width: 899.98px)");
  const [getUserTasks] = useLazyQuery(GET_TASK_FOR_USERS, {
    fetchPolicy: "no-cache",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    checkWebAuthnLocalAndStore();
    getPaymentUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * This effect exists ONLY for the "Get Future Tasks" toggle. The child's
   * `[selectedTaskSortId]` effect (AdminDashboardTasks) already runs the mount
   * fetch, so firing here on mount as well invoked `getTasksForUser` a second
   * time with variables differing only in `time`'s millisecond.
   *
   * It is suppressed on mount rather than DELETED, and that distinction is
   * load-bearing: `handleQueryAllTasks` only flips state — this effect is the
   * SOLE refetch path for the toggle. Deleting it leaves a button that relabels
   * itself and fetches nothing.
   *
   * The guard compares the VALUE rather than tracking "have I run before".
   * A `didMount` boolean is the one guard shape StrictMode inverts: refs
   * survive its simulated unmount, so the first setup would consume the flag
   * and the second would fetch — reinstating the duplicate. Comparing
   * `queryAllTasks` is correct under either behaviour, so this does not quietly
   * depend on `index.js` never adopting StrictMode.
   *
   * (The duplicate was rarely a second HTTP request: `queryDeduplication`
   * defaults to true and collapses same-variable calls at the link regardless
   * of `fetchPolicy`. What this reliably removes is the second
   * setTasks/render, not a round trip.)
   */
  const lastQueryAllRef = useRef(queryAllTasks);
  useEffect(() => {
    if (lastQueryAllRef.current === queryAllTasks) return;
    lastQueryAllRef.current = queryAllTasks;
    getTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryAllTasks]);

  const getTasks = async (sortInc) => {
    let sort = selectedSort;
    const days4 = 1000 * 60 * 60 * 24 * 4;
    const date = queryAllTasks ? null : (Date.now() + days4).toString();

    if (sortInc) {
      sort = sortInc;
      setSelectedSort(sortInc);
    }

    // update tasks
    const { data } = await getUserTasks({
      variables: {
        sort,
        time: date,
      },
    });

    /*
     * ALWAYS leave the `null` state, even on failure. `tasks === null` renders
     * the loading skeleton, so a failed or empty fetch that left it null would
     * shimmer forever — a worse outcome than the "No Tasks To Display" the
     * skeleton replaced. `getTasksForUser` can also return null on its own.
     */
    setTasks(data?.getTasksForUser ? [...data.getTasksForUser] : []);
  };

  /*
   * After an update, drop any task that no longer belongs in the list the user
   * is looking at — a task reassigned away from them should not linger until
   * the next refetch.
   *
   * THE "ALL" EARLY RETURN IS NOT OPTIONAL. `handleChangeAssigneeSort` sets the
   * sort to the literal string `"all"`, which `getTasks` writes into
   * `selectedSort`. No real `assigneeId` is `"all"`, so without this the filter
   * kept only the `"22"`/`""` rows and **every specifically-assigned task
   * vanished from the list** the moment anything was updated while the "All"
   * filter was active. (A `null` sentinel does not fix it either — the
   * predicate tests `assigneeId === idCheck`, and nothing equals `null`.)
   *
   * `""` deliberately still maps to `userId`: the server's default branch is
   * me-or-"22" (resolvers.js:3441), so the client filter has to agree with it.
   *
   * It no longer closes the modal. Closing is the HOST's business — this runs
   * for the inline pane too, where `setSelectedTask("")` would blank the pane
   * the user is still looking at.
   */
  const removeTaskAfterReassignment = (taskArr) => {
    const newTasks = [...taskArr]?.filter((item) => {
      if (selectedSort === "all") return true;
      const idCheck = selectedSort === "" ? userId : selectedSort;
      //"all" id is "22"
      return (
        item?.assigneeId === "22" ||
        item?.assigneeId === idCheck ||
        item?.assigneeId === ""
      );
    });

    setTasks([...newTasks]);
  };

  const getPaymentUrl = () => {
    const paymentId = sessionStorage.getItem("bdr-redirect-payment-id");
    sessionStorage.removeItem("bdr-redirect-payment-id");

    setNoRedirect(!paymentId);

    if (paymentId) {
      navigate(`/bdr-office/payments/process-payment/payment-id/${paymentId}`);
    }
    return;
  };

  const handleQueryAllTasks = () => {
    setQueryAllTasks((prev) => !prev);
  };

  const checkWebAuthnLocalAndStore = async () => {
    const user = await Auth.getProfile();
    if (user?.data?.permission) {
      setUserPerm(user?.data?.permission);
      setUserId(user?.data?._id);
    }
    const localWebAuthn = localStorage.getItem("BDRWebAuthnCredId");

    if (
      (user?.data?.usesWebAuthn && localWebAuthn) ||
      !user?.data?.usesWebAuthn
    ) {
      return;
    }

    const { data } = await getUserWebAuthnIds();

    if (data?.getUserWebAuthnIds?.length > 0) {
      WebAuthnCheckAndStore(data?.getUserWebAuthnIds);
    }

    return;
  };

  const handleTaskModal = (bool) => {
    if (bool === false) {
      setSelectedTask("");
    }
    setOpenSingleTaskModal(bool);
  };

  const handleSelectedTask = (taskId) => {
    setSelectedTask(taskId);
  };

  const handleUpdatedTasksArr = (taskUpdated) => {
    if (taskUpdated) {
      const updatedArr = tasks?.map((task) => {
        if (task?.id === taskUpdated?.id) {
          return taskUpdated;
        }
        return task;
      });

      // setTasks([...updatedArr]);
      removeTaskAfterReassignment(updatedArr);
    }
  };

  return (
    <div className="admin-container">
      {hasAnyRole(userPerm, ['admin', 'office']) && noRedirect && (
        <>
          <TaskDisplayModal
            openModal={openSingleTaskModal}
            setOpenModal={handleTaskModal}
            taskId={selectedTask}
            refetch={getTasks}
            handleUpdatedTasksArr={handleUpdatedTasksArr}
          />
          <AdminDashboardTasks
            queryTasks={getTasks}
            tasks={tasks}
            handleSelectedTask={handleSelectedTask}
            handleUpdatedTasksArr={handleUpdatedTasksArr}
            handleTaskModal={handleTaskModal}
            useTaskModal={below900MQ}
            handleQueryAllTasks={handleQueryAllTasks}
            queryAllTasks={queryAllTasks}
            // removeTaskAfterReassignment={removeTaskAfterReassignment}
          />{" "}
        </>
      )}
      <Tracking removeVisit={true} />
    </div>
  );
};

export default AdminDashboard;
